import { useState, useEffect, useCallback } from 'react';
import apiClient from '@shared/lib/apiClient';

/**
 * useAuth — JWT-based authentication hook
 *
 * Access Token  : localStorage['access_token']  → 用于 API 请求
 * Refresh Token : localStorage['refresh_token'] → 用于续期，存 httpOnly Cookie 更佳（后端配合）
 *
 * 浏览器永远不知道数据库密码，只存 JWT。后端 API 验证 JWT 后才操作 MySQL。
 */
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // 初始化：从后端 /auth/me 加载当前用户（token 由 apiClient 拦截器自动注入）
  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) { setLoading(false); return; }
        const me = await apiClient.get('/auth/me');
        setUser(me ?? null);
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    initAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await apiClient.post('/auth/login', { email, password });
    localStorage.setItem('access_token', data.accessToken);
    localStorage.setItem('refresh_token', data.refreshToken);
    setUser(data.user);
  }, []);

  const signup = useCallback(async (email, password) => {
    const { data } = await apiClient.post('/auth/register', { email, password });
    localStorage.setItem('access_token', data.accessToken);
    localStorage.setItem('refresh_token', data.refreshToken);
    setUser(data.user);
  }, []);

  const verifyOtp = useCallback(async (email, token, type = 'signup') => {
    const { data } = await apiClient.post('/auth/verify-otp', { email, token, type });
    if (data.accessToken) localStorage.setItem('access_token', data.accessToken);
    if (data.refreshToken) localStorage.setItem('refresh_token', data.refreshToken);
    setUser(data.user ?? null);
    return data;
  }, []);

  const sendOtp = useCallback(async (email) => {
    await apiClient.post('/auth/send-otp', { email });
  }, []);

  const oauthLogin = useCallback(async (provider) => {
    // 重定向到后端 OAuth 发起端点
    const { data } = await apiClient.get(`/auth/oauth/${provider}/url`);
    if (data?.url) window.location.href = data.url;
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch { /* ignore */ }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
  }, []);

  const getAccessToken = useCallback(() => {
    return localStorage.getItem('access_token');
  }, []);

  const resetPassword = useCallback(async (email) => {
    await apiClient.post('/auth/reset-password', { email });
  }, []);

  return { user, loading, login, signup, verifyOtp, sendOtp, logout, oauthLogin, getAccessToken, resetPassword };
};
